import { motion } from "framer-motion";
import { fadeUp, fade, stagger } from "../animations";

export default function Home() {
  return (
    <motion.section
      initial="hidden"
      animate="visible"
      variants={fade}
      className="max-w-6xl mx-auto px-6 py-16 space-y-24"
    >

      {/* ================= HERO ================= */}
      <div className="grid md:grid-cols-2 gap-12 items-center">
        {/* LEFT */}
        <motion.div variants={fadeUp}>
          <span className="inline-block text-xs px-4 py-1 border border-blue-400 rounded-full text-blue-300">
            Full Stack • AI/ML • Flutter
          </span>

          <h1 className="text-4xl md:text-5xl font-bold mt-4 leading-tight">
            Building{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-yellow-300">
              intelligent full stack
            </span>{" "}
            solutions
          </h1>

          <p className="text-gray-400 mt-6 max-w-xl">
            Full Stack Developer & AI/ML Engineer with 3+ years of experience,
            building scalable web, mobile, and AI-powered applications.
          </p>

          <div className="flex flex-wrap gap-4 mt-8">
            <motion.a
              whileHover={{ scale: 1.05 }}
              href="/projects"
              className="px-6 py-3 rounded-full bg-gradient-to-r from-blue-400 to-yellow-300 text-black font-semibold"
            >
              View Projects
            </motion.a>

            <motion.a
              whileHover={{ scale: 1.05 }}
              href="/contact"
              className="px-6 py-3 rounded-full border border-yellow-300 text-yellow-300"
            >
              Hire / Collaborate
            </motion.a>

            <motion.a
              whileHover={{ scale: 1.05 }}
              href="/Jeevitha_R_Resume.pdf"
              download
              className="px-6 py-3 rounded-full border border-blue-400 text-blue-300"
            >
              Download Resume
            </motion.a>
          </div>
        </motion.div>

        {/* RIGHT */}
        <motion.div variants={fadeUp} className="flex justify-center">
          <div className="bg-[#0b1020] p-6 rounded-2xl border border-yellow-400/50 text-center max-w-xs">
            <img
              src="/profile.jpg"
              alt="Jeevitha R"
              className="w-32 h-32 mx-auto rounded-full border-4 border-yellow-300 object-cover"
            />
            <h3 className="mt-4 font-semibold text-lg">Jeevitha R</h3>
            <p className="text-gray-400 text-sm">Full Stack & AI/ML Developer</p>
          </div>
        </motion.div>
      </div>

      {/* ================= WHAT I DO ================= */}
      <motion.div variants={fadeUp} className="grid md:grid-cols-3 gap-6">
        {[
          {
            title: "Full Stack Development",
            desc: "Scalable backend APIs and responsive web applications."
          },
          {
            title: "AI & Machine Learning",
            desc: "OCR, NLP, classification models with real-world deployment."
          },
          {
            title: "Mobile App Development",
            desc: "Flutter apps integrated with secure backend services."
          }
        ].map((item) => (
          <div
            key={item.title}
            className="bg-[#0b1020] p-6 rounded-xl border border-white/10"
          >
            <h3 className="font-semibold mb-2">{item.title}</h3>
            <p className="text-gray-400 text-sm">{item.desc}</p>
          </div>
        ))}
      </motion.div>

      {/* ================= QUICK STATS ================= */}
      <motion.div variants={fadeUp} className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
        {[
          ["3+", "Years Experience"],
          ["15+", "Projects Delivered"],
          ["50+", "Students Mentored"],
          ["100%", "Client Satisfaction"]
        ].map(([num, label]) => (
          <div
            key={label}
            className="bg-[#0b1020] p-6 rounded-xl border border-white/10"
          >
            <div className="text-3xl font-bold text-yellow-300">{num}</div>
            <div className="text-sm text-gray-400 mt-1">{label}</div>
          </div>
        ))}
      </motion.div>

      {/* ================= TECH STACK ================= */}
      <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-3">
        {[
          "Python",
          "Flask",
          "FastAPI",
          "Machine Learning",
          "Flutter",
          "React",
          "Tailwind",
          "MySQL"
        ].map((tech) => (
          <span
            key={tech}
            className="px-4 py-1 border border-blue-400 rounded-full text-blue-300 text-sm"
          >
            {tech}
          </span>
        ))}
      </motion.div>

      {/* ================= TESTIMONIALS ================= */}
      <motion.div variants={fadeUp} className="space-y-8">
        <h2 className="text-2xl font-semibold text-center text-yellow-300">
          What People Say
        </h2>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              name: "MCA Student",
              text: "Clear guidance and practical ML project mentoring."
            },
            {
              name: "Intern – Full Stack",
              text: "Learned real-world Flask APIs and deployment."
            },
            {
              name: "Project Client",
              text: "Delivered clean, scalable admin dashboards on time."
            }
          ].map((t, i) => (
            <motion.div
              key={i}
              whileHover={{ y: -6 }}
              className="bg-[#0b1020] p-6 rounded-xl border border-white/10 text-gray-400"
            >
              <p className="text-sm italic">“{t.text}”</p>
              <div className="mt-4 font-semibold text-white">{t.name}</div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* ================= CTA ================= */}
      <motion.div
        variants={fadeUp}
        className="text-center bg-gradient-to-r from-[#1b254b] to-[#050814] p-10 rounded-2xl border border-yellow-400/30"
      >
        <h2 className="text-2xl font-semibold mb-3">
          Let’s build something impactful
        </h2>
        <p className="text-gray-400 mb-6">
          Open to full-time roles, freelance work, and collaborations.
        </p>
        <a
          href="/contact"
          className="inline-block px-8 py-3 rounded-full bg-gradient-to-r from-blue-400 to-yellow-300 text-black font-semibold"
        >
          Contact Me
        </a>
      </motion.div>

    </motion.section>
  );
}
